import { ContentPaste, MoreVert, Receipt, Visibility } from '@mui/icons-material';
import {
    Box,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React from 'react';
import ReviewRequestAppSvg from '../../utility/Svg/ReviewRequestAppSvg';
import ReviewRequestEmailSvg from '../../utility/Svg/ReviewRequestEmailSvg';
import ReviewRequestTextSvg from '../../utility/Svg/ReviewRequestTextSvg';
import { formatPhoneNumber } from '../Common/FormatPhoneNumber';
import ReviewRequestEmptyState from './ReviewRequestEmptyState';

// ── Style constants ────────────────────────────────────────────────────────
const TABLE_SX = { tableLayout: 'auto', width: '100%' };
const ACTION_COL = { width: 56, minWidth: 56, maxWidth: 56 };
const COL = {};
const BODY_CELL = { whiteSpace: 'nowrap' };

const styles = {
    // md+: horizontal scroll only, page handles vertical
    tableContainerMdUp: { overflowX: 'auto', overflowY: 'visible', maxHeight: 'none', width: '100%', padding: '8px 16px', borderRadius: '8px' },
    tableContainerSmDown: { overflow: 'auto', maxHeight: 500, width: '100%', padding: '8px 16px', borderRadius: '8px' },
    actionColHeader: { ...ACTION_COL, fontWeight: 500 },
    emptyCellLoader: { py: 6 },
    actionIconBtn: { background: '#EBEBEB', width: '24px', height: '24px' },
    actionIcon: { width: '18px', height: '18px' },
    menuPaper: {
        borderRadius: '4px!important',
        boxShadow: '0 3px 14px 2px rgba(0,0,0,0.12), 0 8px 10px 1px rgba(0,0,0,0.14), 0 5px 5px -3px rgba(0,0,0,0.20)',
    },
    menuItem: { padding: '6px 10px!important' },
    menuItemIcon: { marginLeft: '8px' },
    // Type chip colours matching Image 1
    typeChip: (type) => {
        const map = {
            apex_auto_app: { bg: '#EDEDED', color: '#002394' },

            shop_created: { bg: 'rgba(117, 219, 255, 0.50)', color: 'rgba(0,0,0,0.87)' },
            shop_imported: { bg: '#E1D0FD', color: '#3E1C76' },
            shop_guest: { bg: '#E1D0FD', color: '#3E1C76' },
            walk_in: { bg: '#FFA969', color: 'rgba(0,0,0,0.87)' },
        };
        const key = (type || '').toLowerCase().replace(/ /g, '_');
        return map[key] || { bg: '#EEEEEE', color: 'rgba(0,0,0,0.87)' };
    },
    chipBase: {
        fontWeight: 500,
        fontSize: '13px',
        lineHeight: '150%',
        letterSpacing: '0.15px',
        padding: '0 6px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '100px',
        width: 'max-content',
    },
    deliveryText: { fontSize: '13px', color: 'text.primary' },
    dotSeparator: { mx: 1, color: '#BDBDBD', fontSize: '16px', lineHeight: 1 },
};

// ── Column definitions ─────────────────────────────────────────────────────
const COLUMNS = [
    { id: 'reviewRequest.requestedAt', label: 'Date Requested', sortable: true, minWidth: 160 },
    { id: 'customer.fullName', label: 'Customer Name', sortable: true },
    { id: 'mobileNumber', label: 'Mobile Number', minWidth: 150 },
    { id: 'email', label: 'Email' },
    { id: 'source', label: 'Source' },
    { id: 'type', label: 'Type' },
    { id: 'deliveryMethod', label: 'Delivery Method', minWidth: 250 },
];

// ── Delivery method icons (text/SVG) ───────────────────────────────────────
const DELIVERY_ICONS = {
    text: <ReviewRequestTextSvg color={'#006CDD'} />,
    email: <ReviewRequestEmailSvg color={'#006CDD'} />,
    app: <ReviewRequestAppSvg color={'#006CDD'} />,
};

const getDeliveryMethodsFromReviewRequest = (reviewRequest) => {
    if (!reviewRequest) return [];
    const methods = [];
    if (reviewRequest?.sms?.sent) methods.push('text');
    if (reviewRequest?.email?.sent) methods.push('email');
    if (reviewRequest?.notification?.sent) methods.push('app');
    return methods;
};

const renderDeliveryMethods = (methods) => {
    if (!methods || methods.length === 0) return '—';
    const parts = Array.isArray(methods) ? methods : [methods];
    return (
        <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0}>
            {parts.map((m, i) => (
                <React.Fragment key={m}>
                    {i > 0 && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none" style={{ margin: '0 9px' }}>
                            <circle cx="2" cy="2" r="2" fill="#BDBDBD" />
                        </svg>
                    )}
                    <Box display="flex" alignItems="center" gap={'3px'}>
                        {DELIVERY_ICONS[(m || '').toLowerCase()] || null}
                        <Typography component="span" sx={styles.deliveryText}>
                            {(m || '').charAt(0).toUpperCase() + (m || '').slice(1)}
                        </Typography>
                    </Box>
                </React.Fragment>
            ))}
        </Stack>
    );
};

const getTypeLabel = (type) => {
    const map = { apex_auto_app: 'Apex Auto', walk_in: 'Walk In', shop_created: 'Shop Created', shop_guest: 'Guest', shop_imported: 'Shop Imported' };
    return map[(type || '').toLowerCase().replace(/ /g, '_')] || type || '—';
};

const getSourceLabel = (requestedFrom) => {
    const map = {
        quickRequest: 'Direct',
        appointment: 'Appointment',
        invoice: 'Invoice',
    };
    return map[requestedFrom] || (requestedFrom ? requestedFrom.charAt(0).toUpperCase() + requestedFrom.slice(1) : '—');
};

// ── Cell renderer ──────────────────────────────────────────────────────────
const renderCell = (colId, row) => {
    switch (colId) {
        case 'customer.fullName':
            return row?.customer?.fullName || `${row?.customer?.firstName || ''} ${row?.customer?.lastName || ''}`.trim() || '—';

        case 'reviewRequest.requestedAt': {
            if (!row?.reviewRequest?.requestedAt) return '—';
            const d = moment(row?.reviewRequest?.requestedAt);
            const today = moment().startOf('day');
            const yesterday = moment().subtract(1, 'days').startOf('day');
            if (d.isSame(today, 'day')) return 'Today';
            if (d.isSame(yesterday, 'day')) return 'Yesterday';
            return d.format('DD-MMM-YYYY');
        }

        case 'mobileNumber':
            return formatPhoneNumber(row?.customer?.phoneNumber) || formatPhoneNumber(row?.phoneNumber) || '—';

        case 'email':
            return row?.customer?.email || row?.email || '—';
        case 'source':
            return getSourceLabel(row?.reviewRequest?.requestedFrom);
        case 'type': {
            if (!row?.customer?.type) return '—';
            const chipStyle = styles.typeChip(row?.customer?.type);
            return (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Chip label={getTypeLabel(row?.customer?.type)} sx={{ ...styles.chipBase, backgroundColor: chipStyle.bg, color: chipStyle.color }} />
                </div>
            );
        }
        case 'deliveryMethod':
            return renderDeliveryMethods(getDeliveryMethodsFromReviewRequest(row?.reviewRequest));
        default:
            return '—';
    }
};

// ─────────────────────────────────────────────────────────────────────────────
const ReviewRequestTable = ({
    filterApplied = false,
    rows = [],
    showLoader,
    totalCount = 0,
    page,
    rowsPerPage,
    sortBy,
    sortType,
    onSort,
    onPageChange,
    onRowsPerPageChange,
    onViewCustomer,
    onViewAppointment,
    onViewInvoice,
    onSendFirstRequest,
    onRowClick,
}) => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const [anchorElDropdown, setAnchorElDropdown] = React.useState(null);
    const [selectedRow, setSelectedRow] = React.useState({});
    const openDropdown = Boolean(anchorElDropdown);

    const handleMenuOpen = (event, row) => {
        event.stopPropagation();
        setAnchorElDropdown(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => setAnchorElDropdown(null);

    return (
        <>
            <Grid container item sx={{ bgcolor: 'white', borderRadius: '8px', boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.10)' }}>
                <Grid item xl={12} md={12} sm={12} xs={12}>
                    <TableContainer sx={isMdUp ? styles.tableContainerMdUp : styles.tableContainerSmDown}>
                        <Table stickyHeader={!isMdUp} aria-label="review requests table" sx={TABLE_SX}>
                            {!filterApplied && rows.length === 0 && !showLoader ? (
                                <></>
                            ) : (
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS.map((col) => (
                                            <TableCell
                                                key={col.id}
                                                align={col.id === 'type' ? 'center' : 'left'}
                                                sx={{ ...COL, fontWeight: 500, ...(col.minWidth ? { minWidth: col.minWidth } : {}) }}
                                            >
                                                {col.sortable ? (
                                                    <TableSortLabel
                                                        active={sortBy === col.id}
                                                        direction={sortType === 'ASC' ? 'asc' : 'desc'}
                                                        onClick={() => onSort(col.id)}
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    col.label
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={styles.actionColHeader} />
                                    </TableRow>
                                </TableHead>
                            )}

                            <TableBody>
                                {showLoader ? (
                                    <TableRow>
                                        <TableCell colSpan={COLUMNS.length + 1} align="center" sx={styles.emptyCellLoader}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ p: 0, border: 0 }}>
                                            {filterApplied ? (
                                                <div className="not-found-class" style={{ height: '35vh', width: '100%' }}>
                                                    Sorry, we could not find any matches.
                                                </div>
                                            ) : (
                                                <ReviewRequestEmptyState onSendFirstRequest={onSendFirstRequest} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow key={row._id} hover sx={{ cursor: 'pointer' }} onClick={() => onRowClick(row)}>
                                            {COLUMNS.map((col) => (
                                                <TableCell key={col.id} align={col.id === 'type' ? 'center' : 'left'} sx={BODY_CELL}>
                                                    {renderCell(col.id, row)}
                                                </TableCell>
                                            ))}
                                            <TableCell align="center" sx={{ ...BODY_CELL, ...ACTION_COL }} onClick={(e) => e.stopPropagation()}>
                                                <Box display="flex" justifyContent="center">
                                                    <IconButton size="small" sx={styles.actionIconBtn} onClick={(e) => handleMenuOpen(e, row)}>
                                                        <MoreVert sx={styles.actionIcon} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {rows.length > 0 && (
                        <Grid container item alignItems={'center'} justifyContent="end">
                            <Grid item>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    colSpan={3}
                                    count={totalCount}
                                    rowsPerPage={rowsPerPage}
                                    page={page > 0 ? page - 1 : 0}
                                    slotProps={{
                                        select: { 'aria-label': 'rows per page' },
                                        actions: { showFirstButton: true, showLastButton: true },
                                    }}
                                    onPageChange={onPageChange}
                                    onRowsPerPageChange={onRowsPerPageChange}
                                />
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {/* ── 3-dot menu ── */}
            <Menu anchorEl={anchorElDropdown} open={openDropdown} onClose={handleMenuClose} sx={{ '& .MuiPaper-root': styles.menuPaper }}>
                {/* {selectedRow?.customer?.type !== 'shop_guest' && ( */}
                <MenuItem
                    sx={styles.menuItem}
                    onClick={() => {
                        onViewCustomer && onViewCustomer(selectedRow);
                        handleMenuClose();
                    }}
                >
                    <Visibility />
                    <span style={styles.menuItemIcon}>View Customer</span>
                </MenuItem>
                {/* )} */}
                {selectedRow?.reviewRequest?.requestedFrom === 'appointment' && selectedRow?.appointment?._id && (
                    <MenuItem
                        sx={styles.menuItem}
                        onClick={() => {
                            onViewAppointment && onViewAppointment(selectedRow);
                            handleMenuClose();
                        }}
                    >
                        <ContentPaste />
                        <span style={styles.menuItemIcon}>View Appointment</span>
                    </MenuItem>
                )}

                {selectedRow?.reviewRequest?.requestedFrom === 'invoice' && (selectedRow?.invoice?._id || selectedRow?.appointment?.invoice) && (
                    <MenuItem
                        sx={styles.menuItem}
                        onClick={() => {
                            onViewInvoice && onViewInvoice(selectedRow);
                            handleMenuClose();
                        }}
                    >
                        <Receipt />
                        <span style={styles.menuItemIcon}>View Invoice</span>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default ReviewRequestTable;
